import Link from 'next/link';
import styled from 'styled-components';
import { useRef, useState, useEffect } from 'react';

import { buildSectionLinkUrl } from 'homepage/utils/buildSectionLinkUrl';
import {
  DESKTOP_PADDING,
  DESKTOP_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE,
  MOBILE_PADDING,
  MOBILE_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE,
} from 'homepage/styles/constants';
import { FeatureFlags } from 'featureFlags/constants';
import { fetchProductsForHomepageModule } from 'homepage/services/homepageApi';
import { handleSectionClick } from 'homepage/utils/handleSectionClick';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { HomepageModuleHeader } from 'homepage/components/sections/HomepageModuleHeader';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { IConstructorUserParams } from 'search/types/ConstructorUserParams';
import { Image } from 'shared/components/Image';
import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';
import { ISearchConfig } from 'homepage/types/SectionSearchConfig';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { setCurrentFeedData } from 'store/actions/homepageActions';
import { useAbortController } from 'shared/hooks/useAbortController';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useIsElementInViewport } from 'shared/hooks/useIsElementInViewport';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';

export interface IProductCarouselRowsSection {
  constructorResponse: IConstructorBrowseResponse;
  constructorUserParams?: IConstructorUserParams;
  moduleTrackingData: IModuleTrackingData;
  rowCount: number;
  searchConfig: ISearchConfig;
  sectionIndex: number;
  subtitle: string;
  title?: string;
}

export const ProductCarouselRowsSection = ({
  constructorResponse,
  constructorUserParams,
  moduleTrackingData,
  rowCount,
  sectionIndex,
  searchConfig,
  subtitle,
  title,
}: IProductCarouselRowsSection) => {
  const storeDispatch = useStoreDispatch();
  const moduleRef = useRef();
  const initialInViewport = useIsElementInViewport(moduleRef);

  const { isLoading } = useStoreSelector((state) => state.homepage);

  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);
  const [products, setProducts] = useState(!isConsumerSearchEnabled ? constructorResponse : null);
  const [isModuleInViewport, setIsModuleInViewport] = useState(initialInViewport);
  const { signal } = useAbortController()();
  const isMounted = useIsMounted();

  useEffect(() => {
    const getProducts = async (abortSignal) => {
      const response = await fetchProductsForHomepageModule(
        searchConfig,
        constructorUserParams,
        rowCount,
        abortSignal,
        isConsumerSearchEnabled,
      );
      if (isMounted()) {
        setProducts(response);
      }
    };

    if (isMounted()) {
      if (!isModuleInViewport) {
        setIsModuleInViewport(initialInViewport);
      } else if (
        !isConsumerSearchEnabled &&
        typeof constructorResponse !== 'undefined' &&
        !products
      ) {
        setProducts(constructorResponse);
      } else if (
        isModuleInViewport &&
        !isLoading &&
        !products &&
        constructorUserParams &&
        (isConsumerSearchEnabled || !constructorResponse)
      ) {
        void getProducts(signal);
      }
    }
  }, [
    initialInViewport,
    isModuleInViewport,
    products,
    constructorResponse,
    isConsumerSearchEnabled,
    constructorUserParams,
  ]);

  useEffect(() => {
    if (products) {
      storeDispatch(setCurrentFeedData({ index: sectionIndex, constructorResponse: products }));
    }
  }, [products]);

  const linkUrl =
    buildSectionLinkUrl(searchConfig, products?.response?.groups, isConsumerSearchEnabled) || '/';

  const handleSectionItemClick = (itemIndex, ptSlug) => {
    sendTrackingEvent('CMS_MODULE_ITEM_TAP', {
      ...moduleTrackingData,
      item_position: itemIndex,
      product_template_slug: ptSlug,
      service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
    });
  };

  return products?.response?.results ? (
    <ProductCarouselRowsSection.Container
      key={`prod-carousel-${sectionIndex}`}
      ref={moduleRef}
      $numOfRows={rowCount}
    >
      <HomepageModuleHeader
        addPadding
        count={products?.response?.total_num_results}
        onClick={() =>
          handleSectionClick({
            ...(moduleTrackingData || {}),
            service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
          })
        }
        linkUrl={linkUrl}
        title={subtitle || title}
      />
      <ProductCarouselRowsSection.LargeMultiRowCarouseel
        data-qa="product_carusel_rows_section_carousel"
        $numOfRows={rowCount}
      >
        {products?.response?.results?.map((result, index) => {
          const resultData: Record<string, any> = result?.data || result;
          return (
            <Link
              key={`prod-carousel-item-${sectionIndex}-${resultData?.slug as string}`}
              href={`/${resultData?.category === 'shoes' ? 'sneakers' : 'apparel'}/${
                resultData?.slug as string
              }`}
              passHref
              prefetch={false}
            >
              <ProductCarouselRowsSection.GridImageContainer
                data-qa="product_carousel_link"
                key={resultData.id}
                onClick={() => {
                  handleSectionItemClick(index, resultData?.slug);
                }}
              >
                <Image
                  alt={`${normalizeValue(result?.value || resultData?.title)}`}
                  sizes="(max-width: 480px) 120px, 180px"
                  src={resultData?.image_url || resultData?.pictureUrl}
                  disableLazyLoad={isModuleInViewport}
                />
              </ProductCarouselRowsSection.GridImageContainer>
            </Link>
          );
        })}
      </ProductCarouselRowsSection.LargeMultiRowCarouseel>
    </ProductCarouselRowsSection.Container>
  ) : (
    <ProductCarouselRowsSection.Container
      key={`prod-carousel-${sectionIndex}`}
      ref={moduleRef}
      $numOfRows={rowCount}
    />
  );
};

ProductCarouselRowsSection.Container = styled.div<{ $numOfRows: number }>`
  margin: 32px 0px;
  width: 100%;
  max-width: 100%;
  min-height: ${({ $numOfRows }) => `${$numOfRows * 128}px`};
  padding: 0px 0px 0px ${MOBILE_PADDING};
  ${hideScrollbars};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 60px 0px;
    padding: 0px 0px 0px ${DESKTOP_PADDING};
    min-height: ${({ $numOfRows }) => `${$numOfRows * 200}px`};
  }
`;

ProductCarouselRowsSection.LargeMultiRowCarouseel = styled.div<{ $numOfRows: number }>`
  display: grid;
  grid-template-rows: repeat(
    ${({ $numOfRows }) => $numOfRows || 1},
    ${MOBILE_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE}px
  );
  grid-auto-flow: column;
  grid-gap: 8px;
  overflow-x: scroll;
  max-width: calc(100vw - ${DESKTOP_PADDING});

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
    overflow-x: scroll;
    max-width: 100vw;
  }

  ${hideScrollbars};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    grid-template-rows: repeat(
      ${({ $numOfRows }) => $numOfRows || 1},
      ${DESKTOP_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE}px
    );
    grid-gap: 16px;
    min-height: 200px;
  }
`;

ProductCarouselRowsSection.GridImageContainer = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  max-height: ${MOBILE_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE}px;
  min-height: ${MOBILE_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE}px;
  height: ${MOBILE_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE}px;
  width: ${MOBILE_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE}px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
    max-height: ${DESKTOP_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE}px;
    min-height: ${DESKTOP_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE}px;
    height: ${DESKTOP_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE}px;
    width: ${DESKTOP_PRODUCT_CAROUSEL_ROW_IMAGE_SIZE}px;
  }
`;
